import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  GetLOAById,
  GetMyUser,
  UploadLOAAttachment,
  UpdateLOA,
  DeleteLOA,
} from "helpers/backend_helper"
import Dropzone from "react-dropzone"

const LOADetails = props => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [loaDetails, setLoaDetails] = useState(null)
  const [user, setUser] = useState(null)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [deletingPdf, setDeletingPdf] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [editedLoa, setEditedLoa] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  console.log(loaDetails)

  useEffect(() => {
    fetchData()
  }, [id])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [loaData, userData] = await Promise.all([
        GetLOAById(id),
        GetMyUser(),
      ])
      setLoaDetails(loaData)
      setUser(userData)
      setEditedLoa(loaData)
      console.log(loaData)
      setIsAdmin(loaData?.role === "Admin")
      setError(null)
    } catch (err) {
      setError(err.message || "Failed to load LOA details")
    } finally {
      setLoading(false)
    }
  }

  const isOwner = () => user?.company?.id === loaDetails?.companyID

  const handlePdfUpload = async file => {
    if (!file) return

    setUploadingPdf(true)
    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("id", id)

      const result = await UploadLOAAttachment(formData)

      await UpdateLOA({ ...loaDetails, attachment: result?.url })
      fetchData()
    } catch (err) {
      setError(err.message || "Failed to upload PDF")
      setUploadingPdf(false)
    }
  }

  const handleDeletePdf = async () => {
    if (!loaDetails?.attachment) return

    setDeletingPdf(true)
    try {
      await UpdateLOA({ ...loaDetails, attachment: null })
      fetchData()
    } catch (err) {
      setError(err.message || "Failed to delete PDF")
    } finally {
      setDeletingPdf(false)
    }
  }

  const handleEdit = () => {
    setEditedLoa(loaDetails)
    setEditModal(true)
  }

  const handleSaveEdit = async () => {
    try {
      await UpdateLOA(editedLoa)
      setEditModal(false)
      fetchData()
    } catch (err) {
      setError(err.message || "Failed to update LOA")
    }
  }

  const handleDelete = async () => {
    try {
      await DeleteLOA(id)
      navigate("/partner-cockpit/loa-list")
    } catch (err) {
      setError(err.message || "Failed to delete LOA")
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("LOA Details")}
          />
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("LOA Details")}
          />
          <div className="alert alert-danger">{error}</div>
        </Container>
      </div>
    )
  }

  if (!loaDetails) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("LOA Details")}
          />
          <div className="alert alert-info">
            {props.t("No LOA details found")}
          </div>
        </Container>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("LOA Details")}
            showBackButton
          />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">
                  {props.t("LOA Information")}
                </h4>
                {isAdmin && (
                  <div>
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={handleEdit}
                    >
                      <i className="bx bx-edit me-1"></i>
                      {props.t("Edit")}
                    </Button>
                    <Button color="danger" onClick={() => setDeleteModal(true)}>
                      <i className="bx bx-trash me-1"></i>
                      {props.t("Delete")}
                    </Button>
                  </div>
                )}
              </div>
              <Table className="table-borderless mb-0">
                <tbody>
                  <tr>
                    <th scope="row" style={{ width: "200px" }}>
                      {props.t("CPN")}:
                    </th>
                    <td>{loaDetails.cpn}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Part Number")}:</th>
                    <td>{loaDetails.mpn}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Manufacturer")}:</th>
                    <td>{loaDetails.manufacturer}</td>
                  </tr>

                  <tr>
                    <th scope="row">{props.t("Supplier")}:</th>
                    <td>{loaDetails.supplier}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Price")}:</th>
                    <td>
                      {loaDetails.price} {loaDetails.currency}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("MOQ")}:</th>
                    <td>{loaDetails.moq} pc.</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Lead Time")}:</th>
                    <td>{loaDetails.leadTime}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Packaging Unit")}:</th>
                    <td>{loaDetails.packagingUnit}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("Notes")}:</th>
                    <td>{loaDetails.notes}</td>
                  </tr>
                  <tr>
                    <th scope="row">{props.t("PDF Document")}:</th>
                    <td>
                      {loaDetails?.attachment ? (
                        <div className="d-flex align-items-center">
                          <i className="bx bx-file-pdf text-danger me-2"></i>
                          <a
                            href={loaDetails.attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="me-2"
                          >
                            {props.t("View LOA Attachment")}
                          </a>
                          {isOwner() && (
                            <Button
                              color="danger"
                              size="sm"
                              onClick={handleDeletePdf}
                              disabled={deletingPdf}
                            >
                              {deletingPdf ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="bx bx-trash"></i>
                              )}
                            </Button>
                          )}
                        </div>
                      ) : isOwner() ? (
                        <Dropzone
                          onDrop={acceptedFiles =>
                            handlePdfUpload(acceptedFiles[0])
                          }
                          accept={{
                            "application/pdf": [".pdf"],
                          }}
                          maxFiles={1}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps()} className="cursor-pointer">
                              <input {...getInputProps()} />
                              <Button color="primary" disabled={uploadingPdf}>
                                {uploadingPdf ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="bx bx-upload me-1"></i>
                                    {props.t("Upload PDF")}
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </Dropzone>
                      ) : (
                        <span className="text-muted">
                          {props.t("No PDF attached")}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
        <ModalHeader toggle={() => setEditModal(false)}>
          {props.t("Edit LOA")}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("CPN")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.cpn || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, cpn: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Part Number")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.mpn || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, mpn: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Manufacturer")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.manufacturer || ""}
                    onChange={e =>
                      setEditedLoa({
                        ...editedLoa,
                        manufacturer: e.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Supplier")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.supplier || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, supplier: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Price")}</Label>
                  <Input
                    type="number"
                    step="0.00001"
                    value={editedLoa?.price || ""}
                    onChange={e =>
                      setEditedLoa({
                        ...editedLoa,
                        price: parseFloat(e.target.value),
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Currency")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.currency || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, currency: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("MOQ")}</Label>
                  <Input
                    type="number"
                    value={editedLoa?.moq || ""}
                    onChange={e =>
                      setEditedLoa({
                        ...editedLoa,
                        moq: parseInt(e.target.value),
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Lead Time")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.leadTime || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, leadTime: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Packaging Unit")}</Label>
                  <Input
                    type="text"
                    value={editedLoa?.packagingUnit || ""}
                    onChange={e =>
                      setEditedLoa({
                        ...editedLoa,
                        packagingUnit: e.target.value,
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{props.t("Notes")}</Label>
                  <Input
                    type="textarea"
                    value={editedLoa?.notes || ""}
                    onChange={e =>
                      setEditedLoa({ ...editedLoa, notes: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModal(false)}>
            {props.t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleSaveEdit}>
            {props.t("Save Changes")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          {props.t("Confirm Deletion")}
        </ModalHeader>
        <ModalBody>
          {props.t(
            "Are you sure you want to delete this LOA? This action cannot be undone."
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            {props.t("Cancel")}
          </Button>
          <Button color="danger" onClick={handleDelete}>
            {props.t("Delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default withTranslation()(LOADetails)
